import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Briefcase,
  ClipboardList,
  Handshake,
  Lightbulb,
  MapPin,
  Sparkles,
  Target,
  Users,
} from 'lucide-react';

const PAGE_BG = '#f4f7fc';

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55 } },
};

const values = [
  {
    title: 'مسئولیت در برابر یادگیرنده',
    desc: 'محصول ما «تجربهٔ یادگیری» است؛ از جزئیات پشتیبانی تا کیفیت محتوا، همه‌چیز روی اعتماد بنا شده است.',
    icon: Handshake,
  },
  {
    title: 'رشد کنار هم',
    desc: 'یادگیری مداوم برای تیم، فراتر از شعار است؛ فضایی برای ایده‌پردازی، بازخورد صادقانه و ارتقای مهارت.',
    icon: Lightbulb,
  },
  {
    title: 'هم‌راستایی با مأموریت',
    desc: 'اگر به مسیر «خودِ حقیقی»، معنا و توسعهٔ فردی باور دارید، احتمالاً با فرهنگ من حقیقی نزدیک‌تر هستید.',
    icon: Target,
  },
];

const areas = [
  {
    title: 'محتوا و آموزش',
    desc: 'تولید محتوا، طراحی یادگیری، همراهی اجرای کارگاه‌ها و پشتیبانی آموزشی دوره‌ها.',
    icon: Sparkles,
  },
  {
    title: 'همراهی مخاطب',
    desc: 'پاسخگویی، هماهنگی رویدادها و تجربهٔ کاربری در مسیر خرید و استفاده از دوره‌ها.',
    icon: Users,
  },
  {
    title: 'فنی و محصول',
    desc: 'توسعهٔ وب و اپلیکیشن، زیرساخت پخش و ابزارهایی که یادگیری را روان‌تر می‌کنند.',
    icon: Briefcase,
  },
];

const applySteps = [
  { n: 1, title: 'خواندن این صفحه', desc: 'با فضای کاری و انتظارات مجموعه آشنا شوید.' },
  { n: 2, title: 'تکمیل فرم استخدام', desc: 'فرم جامع درخواست همکاری را پر کنید؛ حدود ۱۵ تا ۲۰ دقیقه زمان می‌برد و شامل سوابق، مهارت‌ها و چند سؤال سناریویی کوتاه است.' },
  { n: 3, title: 'گفتگو', desc: 'در صورت تطابق، برای مصاحبهٔ کوتاه (آنلاین یا حضوری) با شما هماهنگ می‌شود.' },
  { n: 4, title: 'همکاری آزمایشی', desc: 'در برخی نقش‌ها، دورهٔ همکاری کوتاه پیش از قرارداد نهایی در نظر گرفته می‌شود.' },
];

const CareersPage: React.FC = () => {
  return (
    <div className="min-h-screen pb-16 pt-4 text-[#0f172a]" style={{ backgroundColor: PAGE_BG }} dir="rtl">
      <div className="mx-auto w-full max-w-[min(900px,96vw)] px-3 sm:px-5 lg:px-6">
        <nav
          aria-label="مسیر دسترسی"
          className="mb-3 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] text-slate-500 sm:text-xs"
        >
          <Link to="/" className="font-medium transition hover:text-[#1C3B9B]">
            خانه
          </Link>
          <span className="text-slate-400 opacity-80">›</span>
          <span className="font-semibold text-slate-700">فرصت‌های شغلی</span>
        </nav>

        <motion.header
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
          className="relative overflow-hidden rounded-2xl border border-slate-200/90 bg-white p-5 shadow-[0_18px_40px_-28px_rgba(28,59,155,0.35)] sm:p-7"
        >
          <div className="pointer-events-none absolute -left-20 top-0 h-48 w-48 rounded-full bg-gradient-to-br from-[#7c3aed]/12 to-transparent blur-2xl" />
          <div className="pointer-events-none absolute -right-16 bottom-0 h-40 w-40 rounded-full bg-gradient-to-tl from-[#C11277]/10 to-transparent blur-2xl" />
          <motion.div variants={fadeUp} className="relative inline-flex items-center gap-2 rounded-full bg-[#eef2ff] px-3 py-1">
            <Briefcase className="h-3.5 w-3.5 text-[#1C3B9B]" />
            <span className="text-[11px] font-bold text-[#1C3B9B] sm:text-xs">من حقیقی · پیوستن به تیم</span>
          </motion.div>
          <motion.h1
            variants={fadeUp}
            className="relative mt-3 text-2xl font-extrabold leading-tight text-[#0f172a] sm:text-3xl"
          >
            فرصت‌های <span className="bg-gradient-to-l from-[#1C3B9B] to-[#7c3aed] bg-clip-text text-transparent">شغلی</span>
          </motion.h1>
          <motion.p variants={fadeUp} className="relative mt-3 max-w-2xl text-sm leading-7 text-slate-600 sm:text-[15px]">
            در <strong className="text-slate-800">من حقیقی</strong> کنار افرادی کار می‌کنیم که به آموزش معناگرا، احترام
            به مخاطب و کیفیت پایدار باور دارند. برای ما فقط رزومه مهم نیست؛ نحوهٔ فکر کردن، مسئولیت‌پذیری، نظم، عملکرد
            در چالش و بحران، نتیجه‌گرایی، نوآوری و احترام به فرهنگ سازمانی هم اهمیت دارد — همهٔ این‌ها را در فرم جامع
            درخواست همکاری از شما می‌پرسیم. حتی اگر الان جای خالی مشخصی در سایت نبینید، اطلاعات شما در بایگانی
            توانمندی‌ها نگه داشته می‌شود.
          </motion.p>
          <motion.div variants={fadeUp} className="relative mt-4 inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            <MapPin className="h-4 w-4 shrink-0 text-[#7c3aed]" />
            <span>
              بخشی از همکاری‌ها <strong className="text-slate-800">دورکار</strong> و بخشی <strong className="text-slate-800">ترکیبی</strong>
              است؛ جزئیات هر نقش در گفتگو اعلام می‌شود.
            </span>
          </motion.div>
          <motion.div variants={fadeUp} className="relative mt-5 flex flex-wrap gap-2">
            <Link
              to="/careers/apply"
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-l from-[#1C3B9B] to-[#7c3aed] px-4 py-2.5 text-sm font-bold text-white shadow-[0_12px_24px_-14px_rgba(28,59,155,0.55)] transition hover:opacity-95"
            >
              تکمیل فرم استخدام
              <ClipboardList className="h-4 w-4" />
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:border-[#1C3B9B]/30 hover:text-[#1C3B9B]"
            >
              دربارهٔ من حقیقی
              <ArrowLeft className="h-4 w-4 opacity-70" />
            </Link>
          </motion.div>
        </motion.header>

        <section className="mt-8 grid gap-4 sm:grid-cols-3">
          {values.map((v) => {
            const Icon = v.icon;
            return (
              <div
                key={v.title}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_10px_28px_-22px_rgba(15,23,42,0.2)] sm:p-5"
              >
                <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#fdf2f8] text-[#C11277]">
                  <Icon className="h-5 w-5" strokeWidth={2.2} />
                </div>
                <h2 className="text-sm font-extrabold text-[#0f172a]">{v.title}</h2>
                <p className="mt-2 text-xs leading-6 text-slate-600 sm:text-[13px]">{v.desc}</p>
              </div>
            );
          })}
        </section>

        <section className="mt-10 rounded-2xl border border-slate-200 bg-white p-5 sm:p-7">
          <h2 className="text-lg font-extrabold text-[#0f172a] sm:text-xl">حوزه‌هایی که معمولاً به نیروی تازه نیاز داریم</h2>
          <p className="mt-2 text-sm leading-7 text-slate-600">
            این فهرست جنبهٔ راهنما دارد؛ موقعیت‌های باز بسته به فصل کاری و پروژه‌ها تغییر می‌کند. بهترین راه، ارسال
            رزومه با ذکر حوزهٔ علاقه است.
          </p>
          <ul className="mt-5 grid gap-3 sm:grid-cols-3">
            {areas.map((a) => {
              const Icon = a.icon;
              return (
                <li
                  key={a.title}
                  className="rounded-xl border border-slate-100 bg-gradient-to-b from-slate-50/80 to-white p-4"
                >
                  <Icon className="mb-2 h-5 w-5 text-[#1C3B9B]" strokeWidth={2} />
                  <p className="text-sm font-bold text-slate-900">{a.title}</p>
                  <p className="mt-1.5 text-xs leading-6 text-slate-600">{a.desc}</p>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="mt-10 rounded-2xl border border-slate-200 bg-white p-5 sm:p-7">
          <h2 className="text-lg font-extrabold text-[#0f172a] sm:text-xl">مسیر ارسال درخواست</h2>
          <ol className="mt-5 space-y-4">
            {applySteps.map((s) => (
              <li key={s.n} className="flex gap-3 sm:gap-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#1C3B9B] to-[#7c3aed] text-sm font-black text-white">
                  {s.n}
                </span>
                <div>
                  <p className="text-sm font-extrabold text-slate-900">{s.title}</p>
                  <p className="mt-1 text-xs leading-6 text-slate-600 sm:text-[13px]">{s.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-10 rounded-2xl border border-[#1C3B9B]/15 bg-gradient-to-br from-[#f8fafc] via-white to-[#eef2ff] p-5 sm:p-7">
          <h2 className="text-lg font-extrabold text-[#0f172a] sm:text-xl">فرم استخدام شامل چه بخش‌هایی است؟</h2>
          <p className="mt-2 text-sm leading-7 text-slate-600">
            پاسخ‌های واقعی، کوتاه و مشخص برای ما ارزشمندتر از جواب‌های رسمی و کلی هستند.
          </p>
          <ul className="mt-3 list-disc space-y-2 pr-5 text-sm leading-7 text-slate-700 marker:text-[#7c3aed]">
            <li>اطلاعات تماس، تحصیلات، مهارت‌ها و سوابق شغلی شما.</li>
            <li>چند سؤال دربارهٔ دستاوردها و اثرگذاری واقعی‌تان در تجربه‌های قبلی.</li>
            <li>چند سناریوی کوتاه دربارهٔ بحران، حل مسئله، نوآوری و کار تیمی.</li>
            <li>یک بخش دربارهٔ اینکه چرا مایل به همکاری با «من حقیقی» هستید.</li>
          </ul>
        </section>

        <section className="mt-10 rounded-2xl border border-slate-200 bg-white p-5 text-center sm:p-7">
          <Briefcase className="mx-auto h-8 w-8 text-[#1C3B9B]" strokeWidth={2} />
          <h2 className="mt-3 text-lg font-extrabold text-[#0f172a] sm:text-xl">منتظر شنیدن از شما هستیم</h2>
          <p className="mx-auto mt-2 max-w-lg text-sm leading-7 text-slate-600">
            برای درخواست همکاری، فرم جامع استخدام را تکمیل کنید تا اطلاعات شما به دست تیم منابع انسانی برسد. اگر
            سؤالی دارید، همچنان می‌توانید از صفحهٔ تماس با ما هم پیام بفرستید.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            <Link
              to="/careers/apply"
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-l from-[#1C3B9B] to-[#C11277] px-5 py-2.5 text-sm font-bold text-white transition hover:opacity-95"
            >
              تکمیل فرم استخدام
              <ClipboardList className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-bold text-slate-700 transition hover:border-[#1C3B9B]/30 hover:text-[#1C3B9B]"
            >
              تماس با ما
              <ArrowLeft className="h-4 w-4 opacity-70" />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CareersPage;
